import React, { useState } from 'react';
import { Plus, Trash2, TestTube } from 'lucide-react';
import { JSONPath } from 'jsonpath-plus';
import { Button } from '../../ui/button';
import { Input } from '../../ui/form';
import { Badge } from '../../ui/badge';
import { WorkflowWizardData, RoutingRule } from '../workflow-wizard';

interface OrderRoutingRulesStepProps {
  data: WorkflowWizardData;
  onChange: (updates: Partial<WorkflowWizardData>) => void;
  duplicateRuleIds?: Set<string>;
}

const COMMON_FIELDS = [
  { value: 'totalAmount', label: 'Total Amount', type: 'number' },
  { value: 'submitterId', label: 'Submitter ID', type: 'string' },
  { value: 'divisionId', label: 'Division ID', type: 'string' },
  { value: 'priority', label: 'Priority', type: 'string' },
  { value: 'orderType', label: 'Order Type', type: 'string' },
  { value: 'category', label: 'Category', type: 'string' },
  { value: 'vendor', label: 'Vendor', type: 'string' },
  { value: 'costCenter', label: 'Cost Center', type: 'string' },
  { value: 'jsonPath', label: 'Custom JSON Path', type: 'jsonPath' },
];

const OPERATORS = [
  { value: 'equals', label: 'Equals', types: ['string', 'number'] },
  { value: 'contains', label: 'Contains', types: ['string'] },
  { value: 'greater-than', label: 'Greater Than', types: ['number'] },
  { value: 'less-than', label: 'Less Than', types: ['number'] },
  { value: 'between', label: 'Between', types: ['number'] },
];

export function OrderRoutingRulesStep({ data, onChange, duplicateRuleIds }: OrderRoutingRulesStepProps) {
  const [testOrderData, setTestOrderData] = useState('');
  const [testResult, setTestResult] = useState<{ matches: boolean; message: string } | null>(null);

  const addRule = () => {
    const newRule: RoutingRule = {
      id: `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      field: 'totalAmount',
      operator: 'greater-than',
      value: ''
    };

    onChange({ routingRules: [...data.routingRules, newRule] });
  };

  const removeRule = (ruleId: string) => {
    onChange({
      routingRules: data.routingRules.filter(r => r.id !== ruleId)
    });
  };

  const updateRule = (ruleId: string, updates: Partial<RoutingRule>) => {
    onChange({
      routingRules: data.routingRules.map(r =>
        r.id === ruleId ? { ...r, ...updates } : r
      )
    });
  };

  const getFieldType = (fieldValue: string): string => {
    const field = COMMON_FIELDS.find(f => f.value === fieldValue);
    if (field?.value === 'jsonPath') return 'string'; // JSON path returns string by default
    return field?.type || 'string';
  };

  // Helper to extract value using JSON path with full JSONPath expression support
  const getValueByPath = (obj: any, path: string): any => {
    if (!path) return undefined;
    
    try {
      // Check if this is a JSONPath filter expression (contains [?(...)])
      const isFilterExpression = /\[\?.*\]/.test(path);
      
      if (isFilterExpression) {
        // Use full JSONPath library for complex queries
        // Ensure path starts with $ for JSONPath library
        const jsonPath = path.startsWith('$') ? path : `$.${path}`;
        const results = JSONPath({ path: jsonPath, json: obj });
        
        // Return first result if array, or the result directly
        if (Array.isArray(results) && results.length > 0) {
          return results[0];
        }
        return results;
      } else {
        // Simple path - use basic dot notation/bracket parsing for performance
        const cleanPath = path.replace(/^\$\.?/, '');
        const parts = cleanPath.split(/[.\[\]]+/).filter(p => p);
        
        let value = obj;
        for (const part of parts) {
          if (value === undefined || value === null) return undefined;
          value = value[part];
        }
        
        return value;
      }
    } catch (error) {
      console.error('Error extracting value by path:', error);
      return undefined;
    }
  };

  const getAvailableOperators = (fieldValue: string) => {
    const fieldType = getFieldType(fieldValue);
    return OPERATORS.filter(op => op.types.includes(fieldType));
  };

  const testRules = () => {
    try {
      const orderData = JSON.parse(testOrderData);
      let matches = true;
      let failedRule = '';

      for (const rule of data.routingRules) {
        // Get field value - use JSON path if it's a custom path, otherwise direct property
        const fieldValue = rule.field === 'jsonPath' && rule.valueEnd
          ? getValueByPath(orderData, rule.valueEnd)
          : orderData[rule.field];
        
        const displayField = rule.field === 'jsonPath' ? rule.valueEnd : rule.field;
        
        switch (rule.operator) {
          case 'equals':
            if (String(fieldValue) !== String(rule.value)) {
              matches = false;
              failedRule = `${displayField} does not equal ${rule.value}`;
            }
            break;
          case 'contains':
            if (!String(fieldValue).toLowerCase().includes(String(rule.value).toLowerCase())) {
              matches = false;
              failedRule = `${displayField} does not contain ${rule.value}`;
            }
            break;
          case 'greater-than':
            if (Number(fieldValue) <= Number(rule.value)) {
              matches = false;
              failedRule = `${displayField} is not greater than ${rule.value}`;
            }
            break;
          case 'less-than':
            if (Number(fieldValue) >= Number(rule.value)) {
              matches = false;
              failedRule = `${displayField} is not less than ${rule.value}`;
            }
            break;
          case 'between':
            if (Number(fieldValue) < Number(rule.value) || Number(fieldValue) > Number(rule.valueEnd || 0)) {
              matches = false;
              failedRule = `${displayField} is not between ${rule.value} and ${rule.valueEnd}`;
            }
            break;
        }

        if (!matches) break;
      }

      setTestResult({
        matches,
        message: matches
          ? 'This order would match this workflow!'
          : `Order does not match: ${failedRule}`
      });
    } catch (error) {
      setTestResult({
        matches: false,
        message: 'Invalid JSON format'
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Order Routing Configuration
        </h3>
        <p className="text-sm text-gray-600 dark:text-secondary-400 mb-6">
          Choose how orders should be matched to this workflow.
        </p>
      </div>

      {/* Matching Mode Selector */}
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-semibold text-gray-900 dark:text-white">
          Matching Mode
        </label>
        
        <div className="space-y-3">
          <label className="flex items-start gap-3 p-3 bg-white dark:bg-secondary-800 border-2 border-gray-200 dark:border-secondary-700 rounded-lg cursor-pointer hover:border-blue-300 transition-colors">
            <input
              type="radio"
              name="matchingMode"
              value="none"
              checked={data.matchingMode === 'none'}
              onChange={(e) => onChange({ matchingMode: e.target.value as any })}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900 dark:text-white">Manual Only</div>
              <div className="text-sm text-gray-600 dark:text-secondary-400">
                Workflow must be manually assigned to orders. No automatic matching.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 bg-white dark:bg-secondary-800 border-2 border-gray-200 dark:border-secondary-700 rounded-lg cursor-pointer hover:border-blue-300 transition-colors">
            <input
              type="radio"
              name="matchingMode"
              value="routingRules"
              checked={data.matchingMode === 'routingRules'}
              onChange={(e) => onChange({ matchingMode: e.target.value as any })}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900 dark:text-white">Routing Rules (Simple)</div>
              <div className="text-sm text-gray-600 dark:text-secondary-400">
                Define field-based rules specific to this workflow. Quick and easy setup.
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Show routing rules editor only if routingRules mode is selected */}
      {data.matchingMode === 'routingRules' && (
        <>
          <div className="flex justify-between items-center">
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              Routing Rules
            </h4>
          </div>

          <div className="space-y-4">
            {data.routingRules.map((rule) => {
              const availableOperators = getAvailableOperators(rule.field);
              const fieldType = getFieldType(rule.field);
              const isDuplicate = duplicateRuleIds?.has(rule.id);

              return (
                <div
                  key={rule.id}
                  className={`border rounded-lg p-4 space-y-3 ${
                    isDuplicate 
                      ? 'border-red-500 bg-red-50 dark:bg-red-950 ring-2 ring-red-200' 
                      : 'border-gray-300 dark:border-secondary-600'
                  }`}
                >
                  {isDuplicate && (
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm font-semibold mb-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Duplicate rule - This rule already exists in another workflow
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Field Selection */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-secondary-200 mb-1">
                      Field
                    </label>
                    <select
                      value={rule.field}
                      onChange={(e) => {
                        const newFieldType = getFieldType(e.target.value);
                        const newAvailableOps = OPERATORS.filter(op => op.types.includes(newFieldType));
                        updateRule(rule.id, {
                          field: e.target.value,
                          operator: newAvailableOps[0]?.value as any || 'equals',
                          value: '',
                          valueEnd: undefined
                        });
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    >
                      {COMMON_FIELDS.map(field => (
                        <option key={field.value} value={field.value}>
                          {field.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Operator Selection */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-secondary-200 mb-1">
                      Condition
                    </label>
                    <select
                      value={rule.operator}
                      onChange={(e) => updateRule(rule.id, {
                        operator: e.target.value as any,
                        valueEnd: e.target.value === 'between' ? rule.valueEnd : undefined
                      })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    >
                      {availableOperators.map(op => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Value Input */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-secondary-200 mb-1">
                      Value
                    </label>
                    <input
                      type={fieldType === 'number' ? 'number' : 'text'}
                      value={rule.value}
                      onChange={(e) => updateRule(rule.id, { value: e.target.value })}
                      placeholder={fieldType === 'number' ? '0' : 'Enter value...'}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRule(rule.id)}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 mt-5"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* JSON Path Input (when Custom JSON Path is selected) */}
              {rule.field === 'jsonPath' && (
                <div className="pl-0 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                  <label className="block text-xs font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    JSON Path <span className="text-blue-600 dark:text-blue-400">(required)</span>
                  </label>
                  <input
                    type="text"
                    value={rule.valueEnd || ''}
                    onChange={(e) => updateRule(rule.id, { valueEnd: e.target.value })}
                    placeholder="e.g., $.Order_Attributes[?(@.Name == 'DoDAAC')].Value"
                    className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  />
                  <p className="mt-1 text-xs text-gray-600 dark:text-secondary-400">
                    💡 Examples: <code className="bg-white dark:bg-secondary-800 px-1 py-0.5 rounded">metadata.department</code>, 
                    <code className="bg-white dark:bg-secondary-800 px-1 py-0.5 rounded ml-1">lineItems[0].price</code>, 
                    <code className="bg-white dark:bg-secondary-800 px-1 py-0.5 rounded ml-1">$.Order_Attributes[?(@.Name == 'Status')].Value</code>
                  </p>
                </div>
              )}

              {/* Between Range (Second Value) */}
              {rule.operator === 'between' && (
                <div className="pl-0">
                  <label className="block text-xs font-medium text-gray-700 dark:text-secondary-200 mb-1">
                    End Value
                  </label>
                  <input
                    type="number"
                    value={rule.valueEnd || ''}
                    onChange={(e) => updateRule(rule.id, { valueEnd: e.target.value })}
                    placeholder="End value"
                    className="w-full md:w-1/3 px-3 py-2 text-sm border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                  />
                </div>
              )}
            </div>
          );
        })}

            {data.routingRules.length === 0 && (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 dark:border-secondary-600 rounded-lg">
                <p className="text-gray-500 dark:text-secondary-400 mb-2">No routing rules defined</p>
                <p className="text-sm text-gray-500 dark:text-secondary-400 mb-4">
                  Click "Add Routing Rule" to get started
                </p>
              </div>
            )}

            <Button
              variant="outline"
              onClick={addRule}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Routing Rule
            </Button>
          </div>

          {/* Rule Examples */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 mb-2">
              Common Routing Rule Examples
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>High-value orders:</strong> Total Amount &gt; 10000</li>
              <li>• <strong>IT purchases:</strong> Category = "IT Equipment"</li>
              <li>• <strong>Specific vendor:</strong> Vendor contains "Acme"</li>
              <li>• <strong>Mid-range orders:</strong> Total Amount between 1000 and 5000</li>
              <li>• <strong>Custom field:</strong> Custom JSON Path: <code className="bg-blue-100 dark:bg-blue-900 px-1">metadata.department</code> = "Engineering"</li>
              <li>• <strong>Line item check:</strong> Custom JSON Path: <code className="bg-blue-100 dark:bg-blue-900 px-1">lineItems[0].category</code> contains "Software"</li>
              <li>• <strong>Array filter:</strong> Custom JSON Path: <code className="bg-blue-100 dark:bg-blue-900 px-1">$.Order_Attributes[?(@.Name == 'DoDAAC')].Value</code> = "M94700"</li>
            </ul>
          </div>

          {/* Test Rules */}
          {data.routingRules.length > 0 && (
            <div className="border-t border-gray-200 dark:border-secondary-700 pt-6 space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Test Your Rules
                </h4>
                <p className="text-xs text-gray-600 dark:text-secondary-400 mb-3">
                  Enter sample order data as JSON to test if it matches your routing rules
                </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-secondary-200 mb-1">
              Sample Order Data (JSON)
            </label>
            <textarea
              value={testOrderData}
              onChange={(e) => setTestOrderData(e.target.value)}
              placeholder={`{\n  "totalAmount": 15000,\n  "category": "Office Supplies",\n  "vendor": "Acme Corp",\n  "metadata": {\n    "department": "Engineering"\n  },\n  "Order_Attributes": [\n    {"Name": "DoDAAC", "Value": "M94700"},\n    {"Name": "CostCenter", "Value": "CC-1234"}\n  ]\n}`}
              rows={8}
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-secondary-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
            />
          </div>

          <Button
            variant="outline"
            onClick={testRules}
            disabled={!testOrderData.trim()}
          >
            <TestTube className="w-4 h-4 mr-2" />
            Test Rules
          </Button>

          {testResult && (
            <div
              className={`p-4 rounded-lg border ${
                testResult.matches
                  ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
              }`}
            >
              <div className="flex items-center gap-2">
                {testResult.matches ? (
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
                <span
                  className={`text-sm font-medium ${
                    testResult.matches ? 'text-green-900' : 'text-red-900'
                  }`}
                >
                  {testResult.message}
                </span>
              </div>
            </div>
          )}
          </div>
        )}
        </>
      )}

      {/* Summary (only for routingRules mode) */}
      {data.matchingMode === 'routingRules' && data.routingRules.length > 0 && (
        <div className="bg-gray-50 dark:bg-secondary-900 border border-gray-200 dark:border-secondary-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            Routing Rules Summary
          </h4>
          <p className="text-sm text-gray-600 dark:text-secondary-400 mb-3">
            Orders will match this workflow when <strong>all</strong> of the following conditions are met:
          </p>
          <ul className="space-y-1">
            {data.routingRules.map((rule, index) => {
              const field = COMMON_FIELDS.find(f => f.value === rule.field);
              const operator = OPERATORS.find(o => o.value === rule.operator);
              const displayField = rule.field === 'jsonPath' 
                ? `Path: ${rule.valueEnd || '(not set)'}` 
                : field?.label;
              
              return (
                <li key={rule.id} className="text-sm text-gray-700 dark:text-secondary-200 flex items-start gap-2">
                  <Badge className="mt-0.5">{index + 1}</Badge>
                  <span>
                    <strong>{displayField}</strong> {operator?.label.toLowerCase()}{' '}
                    <strong>{rule.value}</strong>
                    {rule.operator === 'between' && rule.valueEnd && rule.field !== 'jsonPath' && (
                      <> and <strong>{rule.valueEnd}</strong></>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
